import React, { InputHTMLAttributes, useRef } from "react";
import styles from "./FileUploadButton.module.scss";
import Resizer from "react-image-file-resizer";
import { ApiUtil } from "utils";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  height?: number;
  text?: string;
  children?: JSX.Element;
  fileUpload?: (data: { files: string[]; fileData: File[] }) => void;
  callback?: () => void;
  uploadUrl?: string;
  uploadFileUrl?: string;
  type?: "image" | "excel" | "all";
  backgroundColor?: string;
  fontColor?: string;
  isThumbnail?: boolean;
  loading?: () => void;
  loadingClose?: () => void;
  containerClassname?: string;
}

const resizeFile = ({
  file,
  maxWidth = 1000,
  maxHeight = 1000,
  format = "JPEG",
  quality = 80,
  rotate = 0,
  outputType = "file",
}: {
  file: File;
  maxWidth?: number;
  maxHeight?: number;
  format?: "JPEG" | "PNG" | "WEBP";
  quality?: number;
  rotate?: number;
  outputType?: "file" | "base64" | "blob";
}) => {
  return new Promise((res) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      format,
      quality,
      rotate,
      (uri) => res(uri), // responseUriFunc
      outputType,
    );
  });
};

export function FileUploadButton({
  text,
  children,
  fileUpload,
  callback,
  uploadUrl,
  uploadFileUrl,
  height,
  type = "image",
  backgroundColor,
  fontColor,
  isThumbnail = false,
  loading,
  loadingClose,
  containerClassname,
  disabled = false,
  accept,
  ...rest
}: IProps) {
  const ref = useRef<HTMLInputElement>(null);

  function fileSelect() {
    if (ref.current) {
      ref.current.files = null;
    }
    ref.current?.click();
  }

  async function uploadFile(event: InputHTMLAttributes<HTMLInputElement>) {
    try {
      if (disabled) {
        return;
      }
      if (!event) {
        throw Error("event is null");
      }
      if (!uploadUrl) {
        throw Error("uploadUrl is null");
      }

      const files = ref.current?.files;
      if (!files || files.length === 0) {
        throw Error("files is null");
      }

      loading?.();

      // 결과를 모아둘 배열
      const uploadFiles: string[] = [];
      const fileList: File[] = [];

      // files는 FileList라 Array.from()으로 배열화
      for (const file of Array.from(files)) {
        const fileName = file.name.toLowerCase();
        const mimeType = file.type;

        if (mimeType.startsWith("image/")) {
          // 이미지 처리
          const resized = (await resizeFile({ file })) as File;

          const data = await ApiUtil.imageUpload({
            files: [resized],
            endPoint: uploadUrl,
          });

          if (!data?.img || data.img.length === 0) {
            throw Error(`사진 업로드 실패: ${fileName}`);
          }

          fileList.push(resized);
          uploadFiles.push(data.img[0]);
        } else {
          // 기타 파일 처리
          if (!uploadFileUrl) {
            continue;
          }

          const { data, message } = await ApiUtil.fileUpload({
            files: [file],
            endPoint: uploadFileUrl,
          });
          // @ts-ignore
          const files = data?.files || [];
          fileList.push(file);
          uploadFiles.push(files[0]);
        }
      }

      // 필요하면 각각 콜백 실행
      fileUpload?.({
        fileData: fileList,
        files: uploadFiles,
      });

      ref.current.files = null;
      ref.current.value = "";
      callback?.();
      loadingClose?.();
    } catch (error) {
      console.log("FileUploadButton uploadFile error", error);
      loadingClose?.();
    }
  }

  return (
    <div
      className={[styles.inputLabel, containerClassname].join(" ")}
      onClick={fileSelect}
    >
      <button
        style={{ fontSize: 12, height, backgroundColor, color: fontColor }}
        disabled={disabled}
      >
        {typeof text === "string" ? text : children}
      </button>
      <input
        {...rest}
        ref={ref}
        type="file"
        onChange={uploadFile}
        disabled={disabled}
        accept={
          type === "image"
            ? "image/*"
            : type === "excel"
            ? "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : accept || "*"
        }
      />
    </div>
  );
}
